import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    Paper,
    Typography,
    Grid,
    Divider,
    TextField,
    ButtonGroup,
    Button,
    FormControl,
    InputLabel,
    Select,
    Chip,
    MenuItem,
} from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';
import { useTheme } from '@mui/material/styles';

import { ColorPicker } from 'mui-color';

import {
    toggleMarkerDrafting, relocateMarkerDraftCoords, removeMarkerDraftCoords,
    markerDraftCoordsSelector, updateMarkerDraftColor,
    saveMarker, markerEditDataSelector,
    setMarkerDetailView, editMarker, removeMarkerDetailView
} from '../../features/places/placesSlice';
import { setNeedBounding } from '../../features/map/mapSlice';
import { tagList } from '../../utils/placeTagsOptions';

const useStyles = makeStyles((theme) => ({
    formControl: {
        margin: useTheme().spacing(1),
        width: 200,
    },
    label: {
        margin: useTheme().spacing(1),
    },
    selectEmpty: {
        marginTop: useTheme().spacing(2),
    },
    margin: {
        margin: useTheme().spacing(1),
    },
    textField: {
        width: '25ch',
    },
    chip: {
        margin: useTheme().spacing(0.5),
    },
}));

const MarkerCreateForm = ({ setListViewCallback }) => {

    const classes = useStyles();
    const dispatch = useDispatch();
    const markerDraftCoords = useSelector(markerDraftCoordsSelector);
    const markerEditData = useSelector(markerEditDataSelector);

    const [markerId, setMarkerId] = useState(null);
    const [markerName, setMarkerName] = useState("");
    const [markerDesc, setMarkerDesc] = useState("");
    const [markerTags, setMarkerTags] = useState([]);
    const [markerColor, setMarkerColor] = useState("#00008B");

    const [formMode, setFormMode] = useState("create");
    //const [validationError, setValidationError] = useState("");


    useEffect(() => {
        dispatch(toggleMarkerDrafting(true));
        return () => {
            dispatch(toggleMarkerDrafting(false));
        }
    }, [dispatch]);



    const loadEditData = useCallback(
        () => {
            //console.log("In Load edit", markerEditData);
            setMarkerId(markerEditData.marker_id);
            setMarkerName(markerEditData.marker_name);
            setMarkerTags(markerEditData.marker_tags ?? []);
            setMarkerColor(markerEditData.marker_color);
            setMarkerDesc(markerEditData.marker_desc);
            dispatch(relocateMarkerDraftCoords(markerEditData.marker_coords));
            dispatch(updateMarkerDraftColor(markerEditData.marker_color));
        },
        [dispatch, markerEditData],
    )

    useEffect(() => {
        if (markerEditData.marker_id) {
            setFormMode("edit");
            dispatch(setMarkerDetailView(markerEditData.marker_id));
            dispatch(setNeedBounding(true));
            loadEditData();
        } else {
            setFormMode("create");
            dispatch(removeMarkerDetailView());
            dispatch(removeMarkerDraftCoords());
            setMarkerName("");
            setMarkerTags([]);
            setMarkerColor("#00008B");
            setMarkerDesc("");
        }
    }, [markerEditData, loadEditData, dispatch]);

    const colorChange = (value) => {
        //console.log("color onChange=", value);
        setMarkerColor(`#${value.hex}`);
        dispatch(updateMarkerDraftColor(`#${value.hex}`));
    }


    const onTagChange = (e) => {
        let tags = [...markerTags];
        if (tags.indexOf(e.target.value) === -1) {
            //console.log("tags before push", tags, e.target.value);
            tags.push(e.target.value);
        }
        setMarkerTags(tags);
    }

    const tagDeselect = (chip) => {
        const index = markerTags.indexOf(chip);
        if (index !== -1) {
            let tags = [...markerTags];
            tags.splice(index, 1);
            setMarkerTags(tags);
        }
    }


    const resetForm = () => {
        if (formMode === "create") {
            dispatch(removeMarkerDraftCoords());
            setMarkerName("");
            setMarkerTags([]);
            setMarkerColor("#00008B");
            setMarkerDesc("");
        } else {
            loadEditData();
        }
    }

    const cancelEdit = () => {
        dispatch(editMarker({}));
        dispatch(removeMarkerDetailView());
        dispatch(removeMarkerDraftCoords());
        setMarkerName("");
        setMarkerTags([]);
        setMarkerColor("#00008B");
        setMarkerDesc("");
        setListViewCallback('markerList');
    }

    const submit = () => {
        dispatch(saveMarker({
            marker_name: markerName,
            marker_coords: markerDraftCoords,
            marker_tags: markerTags,
            marker_id: markerId,
            marker_color: markerColor,
            marker_desc: markerDesc,
        }))
        setListViewCallback('markerList');
    }


    const validator = () => {
        let validationFail = false;
        //let message = "";
        const nameRegex = new RegExp(/^([a-zA-Z0-9 _-]+)$/i);
        if (Object.keys(markerDraftCoords).length !== 2) {
            validationFail = true;
        }
        if (markerName.length > 30 || markerName.length < 4) {
            validationFail = true;
        }
        if (!nameRegex.test(markerName)) {
            validationFail = true;
        }
        return validationFail;

    }

    return (
        <Paper elevation={3}>
            <div style={{ margin: '10px' }}>
                <Grid container spacing={1}>
                    <Grid item sm={12}><Typography variant="h6">Create Marker</Typography></Grid>
                    <Grid item sm={12}><Divider /></Grid>
                    <Grid item sm={12}>
                        <FormControl className={classes.formControl}>
                            <TextField
                                variant="standard"
                                id="markerName"
                                label="Name of the Marker"
                                value={markerName}
                                onChange={e => setMarkerName(e.target.value)}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item sm={12}>
                        <FormControl className={classes.formControl}>
                            <TextField
                                variant="standard"
                                id="markerDesc"
                                label="Desc of the Marker"
                                value={markerDesc}
                                onChange={e => setMarkerDesc(e.target.value)}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item sm={12}>
                        <FormControl className={classes.formControl}>
                            <InputLabel id="reportTypeLabel">Marker Tags</InputLabel>
                            <Select
                                variant="standard"
                                labelId="reportTypeLabel"
                                id="reportTypeSelect"
                                onChange={onTagChange}
                                value={""}
                            >
                                {
                                    tagList.map(ev => <MenuItem key={ev} value={ev}>{ev}</MenuItem>)
                                }
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item sm={12}>
                        {markerTags.map((v, i) =>
                            <Chip
                                size="small"
                                label={v}
                                onDelete={() => tagDeselect(v)}
                                className={classes.chip}
                                key={i}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                    </Grid>

                    <Grid item container sm={12}>
                        <FormControl className={classes.formControl}>
                            <Typography variant="body1"><span style={{ color: markerColor }}>Marker Color</span></Typography>
                            <ColorPicker defaultValue="transparent" value={markerColor} onChange={colorChange} hideTextfield />
                        </FormControl>
                    </Grid>

                    <Grid item sm={12}>
                        <FormControl className={classes.formControl}>
                            <Typography variant="body1"><b>Marker Placed:</b> {Object.keys(markerDraftCoords).length !== 2 ? "No" : "Placed"}</Typography>
                        </FormControl>
                    </Grid>

                    <Grid item sm={12}>
                        <ButtonGroup variant="contained">
                            <Button onClick={() => cancelEdit()} color="secondary">Cancel</Button>
                            <Button onClick={resetForm}>Reset</Button>
                            <Button onClick={submit} disabled={validator()} color="primary">Submit</Button>
                        </ButtonGroup>
                    </Grid>
                    <Grid item sm={12}></Grid>
                </Grid>
            </div>

        </Paper>
    );
}

export default MarkerCreateForm;